import SimpleModal from 'common/Form/Form';
import { Box, Button, Grid, Tooltip, Typography, useMediaQuery } from '@mui/material';
import Card from 'common/Cards/CardDetailWithPricing';
import { useTranslation } from 'react-i18next';
import CardSummary from 'common/Cards/CardSummary';
import ComponentHeaderTwo from 'common/ComponentHeaderTwo/ComponentHeaderTwo';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { InvoiceProfileService } from 'subModule/src/core/services';
import { GetDeliveryNoteByIdResponse } from 'subModule/src/services/invoice/interface';
import AlertMessages from 'common/Alert/AlertMessages';
import Loader from 'layout/Loader';
import PersonIcon from "@mui/icons-material/Person";
import { ReactComponent as Call } from "assets/Modal/call.svg";
import { brandingInfo } from 'features/server/brandingSlice';
import { useSelector } from 'react-redux';
import { themes } from 'theme/themes';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DropdownMenu from 'common/DropDownMenu/DropDownMenu';
import { useGetInvoiceTemplateService, useGetReviewByIdService } from 'subModule/src/services/invoice/useInvoices';
import InvoiceDownloadIcon from "assets/download-invoice";
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';

export default function DeliveryNoteModal() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const params = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const branding = useSelector(brandingInfo);
    const themeColors =
        branding?.attributes?.themeId === 100
          ? branding?.attributes?.customTheme
          : (themes as any)[branding?.attributes?.themeId || 3];
    const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
    const [reviewLoading, setReviewLoading] = useState<boolean>(true);
    const [deliveryNote, setDeliveryNote] = useState<any>({});
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const isScreen900pxOrGreater = useMediaQuery("(min-width:900px)");
    const { data: invoiceTemplatesData, refetch: invoiceTemplatesRefetch } = useGetInvoiceTemplateService({ pageNumber: 1, size: 50, type: 6 });
    const { data: reviewByIdData, refetch: reviewByIdFetch } = useGetReviewByIdService(Number(params?.id));
    const invoiceTemplates = (invoiceTemplatesData?.pages[0]?.data?.content || []).map(
        (item: { id: number; name: string }) => ({
            value: item.id,
            label: item.name,
        })
    );
    useEffect(() => {
        invoiceTemplatesRefetch();
        setTimeout(() => setReviewLoading(true), 50);
        reviewByIdFetch().finally(() => setReviewLoading(false));
    }, []);
    useEffect(() => {
        if (params?.id && invoiceTemplatesData) {
            if (loading) getByID();
        }
    }, [params, invoiceTemplatesData]);
    useEffect(() => {
        if(reviewByIdData && reviewLoading) setReviewLoading(false);
    }, [reviewByIdData]);
    const getByID = () => {
        InvoiceProfileService.getDeliveryNoteByID(+(params as any)?.id).then((res: GetDeliveryNoteByIdResponse) => {
            if (res.success) {
                setDeliveryNote(res?.data || {});
                setLoading(false);
            } else {
                enqueueSnackbar(<AlertMessages text={res.message || "Something went wrong"} />, { variant: 'error' });
            }
        }).catch((err: any) => { console.error("===", err); });
    }
    return (
        <div>
            <ComponentHeaderTwo
                showBtn={true}
                showSaveBtn={false}
                onClick={() => navigate(-1)}
                onSubmit={() => { }}
                heading={`${t('deliveryNote')} ${t('Details')}`}
                icon={<NoteAltOutlinedIcon sx={{ fill: '#000', width: '30px', height: '30px', marginTop: '3px' }} />}
                btnText={"back"}
                btnType={"back"}
                isBtnEnabled={false}
                showCreateBtn={false}
            />
            {(loading || reviewLoading) ? <Loader /> : <SimpleModal
                visable={true}
                title={(`${t('deliveryNote')} ${t('Details')}`).replace(/\s+/g, ' ').trim()}
                isButtonVisable={false}
                isVisableBtn={false}
                btnclosetext="Back"
                buttonText={""}
                attBtnNotshow={false}
                notVisableBackbtn={true}
                formSubmit={() => { }}
                content={<>
                    <Box className='text-end'>
                        <Button
                            id="import-button"
                            variant="contained"
                            disableElevation
                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
                            endIcon={<KeyboardArrowDownIcon />}
                            className={`bg-white font-Saira font-[600] text-[13px] h-[31px] px-4 py-1 cursor-pointer rounded-[6px] ms-[10px]`} 
                            sx={{ outline: '2px solid', color: themeColors?.primary, borderColor: themeColors?.primary }}
                            >{t('download')}
                        </Button>
                        <DropdownMenu anchorEl={anchorEl} menuItems={(invoiceTemplates || []).map((template: { value: number; label: string }) => ({
                                label: (<Box className='flex items-center gap-2'>
                                    <InvoiceDownloadIcon
                                        primary={themeColors?.primary}
                                        secondary={themeColors?.secondary}
                                    />
                                    {t(template?.label || '')}
                                </Box>),
                                onClick: () => navigate(`/delivery-notes/${params?.id}/template/${template.value}`)
                            }))}
                            onClose={() => setAnchorEl(null)} hoverColor={themeColors?.primary}
                        />
                    </Box>
                    <Grid container sx={{ background: "#fff", borderRadius: "8px", margin: "10px 0", padding: "6px 20px", }}>
                        <Grid container xs={12} md={6}>
                            <Grid container xs={12} sm={4}>
                                <Grid xs={12} className="text-left">
                                    <Typography className="font-Saira text-black font-[600] line-clamp-1 text-[14px]">
                                        {deliveryNote?.vehiclePlateNo ? (
                                            <h4 className="font-medium mb-0 text-[12px] ff-primary truncate">
                                                {deliveryNote?.attributes?.makeTitle}-
                                                {deliveryNote?.attributes?.modelTitle}
                                                {deliveryNote?.attributes?.year
                                                    ? "-" + deliveryNote?.attributes?.year
                                                        : ""}
                                            </h4>
                                        ) : (
                                            <h4 className="font-medium mb-0 text-[12px] ff-primary truncate">
                                                {deliveryNote?.attributes?.cartNo || " "}
                                            </h4>
                                        )}
                                    </Typography>
                                </Grid>
                                <Grid xs={12} className="text-left pr-5">
                                    <Tooltip title={deliveryNote?.vehiclePlateNo || t('N/A')}>
                                        <div
                                            className="h-[40px] border-2 rounded-md px-2 flex items-center justify-center bg-[#E5E5E5]"
                                            style={{ borderColor: themeColors?.primary }}
                                        >
                                            <Typography
                                                className="font-Saira line-clamp-1 font-[700] text-[12px]"
                                                sx={{ color: themeColors?.primary }}
                                            >
                                                {deliveryNote?.vehiclePlateNo || t('N/A')}
                                            </Typography>
                                        </div>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                xs={12}
                                sm={6}
                                rowGap={1}
                                className={`relative ${isScreen900pxOrGreater && "left-[32%]"}`}
                            >
                                <Grid xs={12} className="text-left ">
                                    <Typography className={`font-Saira italic text-txt-color font-[400] text-[12px] flex gap-1 justify-end ${i18n?.language === 'ar' || i18n?.language === 'ur' ? 'flex-row-reverse' : 'flex-row'}`}>
                                        {t('deliveryNoteNo')}
                                        <span> : </span>
                                        <span className="font-[700]">{deliveryNote[`deliveryNoteNo`]}</span>
                                    </Typography>
                                </Grid>
                                <Grid container xs={12} rowGap={1} className="text-left">
                                    <Grid item container xs={8}>
                                        <Grid xs={12} sx={{ lineHeight: 0 }}>
                                            <Typography className="font-Saira flex italic text-txt-color font-[500] text-[11px] items-center ms-[-3px]">
                                                {
                                                    <PersonIcon className="mr-1 w-[15px] text-[#000]" />
                                                }{" "}
                                                {deliveryNote?.attributes?.customerName || deliveryNote?.attributes?.companyName || t('N/A')}
                                            </Typography>
                                        </Grid>
                                        <Grid container xs={12}>
                                            <Typography className="font-Saira flex italic text-txt-color font-[500] text-[11px]">
                                                {
                                                    <Call
                                                        height={"12px"}
                                                        className="mt-[2px] mr-1"
                                                        width={"12px"}
                                                    />
                                                }{" "}
                                                {deliveryNote?.attributes?.customerPhone || t('N/A')}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid container xs={12} sm={6}>
                            <Grid xs={12}>
                                <Box className={`font-Saira text-txt-color font-[500] text-[12px] flex gap-1 justify-self-end ${i18n?.language === 'ar' || i18n?.language === 'ur' ? 'flex-row-reverse' : 'flex-row'}`}>
                                    <span className="italic">
                                        {t("vehicleNumber")}
                                    </span>
                                    <span>:</span>
                                    <span className="font-Saira italic text-black font-[700]">
                                        {deliveryNote?.vehicleVin || t('N/A')}
                                    </span>
                                </Box>
                            </Grid>
                            <Grid xs={12}>
                                <Box className={`font-Saira text-txt-color font-[500] text-[12px] flex gap-1 justify-self-end ${i18n?.language === 'ar' || i18n?.language === 'ur' ? 'flex-row-reverse' : 'flex-row'}`}>
                                    <span className="italic">
                                        {t("dueAmount")}
                                    </span>
                                    <span>:</span>
                                    <span className="font-Saira italic text-black font-[700]">
                                        {new Intl.NumberFormat('en', {
                                            notation: 'standard',
                                            maximumFractionDigits: 2,
                                        }).format((deliveryNote as any)?.balanceAmount || 0)}
                                    </span>
                                </Box>
                            </Grid>
                            <Grid container xs={12}>
                                <Grid xs={12} sm={6}>
                                    <span className={`${deliveryNote?.invoiceStatus === 2
                                            ? "unpaid"
                                            : "paid"
                                        } px-2 py-1 sm:px-3 sm:py-2 rounded-lg font-medium uppercase text-sm sm:text-[12px] font-Saira`}
                                    >
                                        {deliveryNote?.invoiceStatus === 2
                                            ? t("pendingInvoice")
                                                : t("invoiced")}
                                    </span>
                                </Grid>
                                <Grid xs={12} sm={6} className="text-right">
                                    <Grid xs={12}>
                                        <Box className={`font-Saira text-txt-color mb-[-5px] text-[9px] font-[500] flex gap-1 justify-self-end ${i18n?.language === 'ar' || i18n?.language === 'ur' ? 'flex-row-reverse' : 'flex-row'}`}>
                                            <span className="italic">
                                                {t("station")}
                                            </span>
                                            <span>:</span>
                                            <span className="font-[600]">
                                                {deliveryNote?.attributes?.stationName}
                                            </span>
                                        </Box>
                                    </Grid>
                                    <Grid xs={12}>
                                        <span className="font-Saira italic text-txt-color font-[500] text-[9px]">
                                            {t("createdAt") + ":"}
                                        </span>
                                        <span className="font-Saira text-txt-color font-[600] text-[11px] ml-2">
                                            {(() => {
                                                const date = new Date(deliveryNote?.createdAt);
                                                return isNaN(date.getTime())
                                                    ? "-"
                                                    : new Intl.DateTimeFormat(i18n.language, {
                                                        year: "numeric",
                                                        month: "long",
                                                        day: "numeric",
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                        second: "2-digit",
                                                        numberingSystem:
                                                            i18n.language === "ur" || i18n.language === "ar"
                                                                ? "arab"
                                                                : "latn",
                                                    }).format(date);
                                            })()}
                                        </span>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        {deliveryNote?.deliveryNotePackages?.length > 0 &&
                            <Grid item xs={12} className="mt-4 p-2 rounded-lg ml-2">
                                <Typography className="font-Saira text-start text-black line-clamp-1 font-[600] mb-2  text-[16px] leading-[24px]" >
                                    {t("packageDetails")}
                                </Typography>
                                <Grid item xs={12}>
                                    <Card data={deliveryNote?.deliveryNotePackages} type="package" typeName='invoice' />
                                </Grid>
                            </Grid>
                        }
                        {deliveryNote?.deliveryNoteServices?.length > 0 &&
                            <Grid item xs={12} className="mt-4 p-2 rounded-lg ml-2">
                                <Typography className="font-Saira text-start text-black line-clamp-1 font-[600] mb-2  text-[16px] leading-[24px]" >
                                    {t("serviceDetails")}
                                </Typography>
                                <Card data={deliveryNote?.deliveryNoteServices} type="service" typeName='invoice' />
                            </Grid>
                        }
                        {deliveryNote?.deliveryNoteItems?.length > 0 &&
                            <Grid item xs={12} className="mt-4 p-2 rounded-lg ml-2">
                                <Typography className="font-Saira text-start text-black line-clamp-1 font-[600] mb-2 text-[16px] leading-[24px]" >
                                    {t("itemDetails")}
                                </Typography>
                                <Grid item xs={12}>
                                    <Card data={deliveryNote?.deliveryNoteItems} type="item" typeName='invoice' />
                                </Grid>
                            </Grid>
                        }
                        <CardSummary data={deliveryNote} typeName="invoice" review={reviewByIdData?.data || {}} />
                    </Grid>
                </>}
            />}
        </div>
    );
}
